/**
 * AdempimentoTypeSelector — F-004 Phase 3 (IMPL-P3-001).
 *
 * Selettore del sottotipo di adempimento (proroga | risoluzione | cessione)
 * reso come vero `role="radiogroup"` navigabile da tastiera (frecce), NON come
 * un set di `<div>` cliccabili.
 *
 * Implementazione: shadcn `RadioGroup`/`RadioGroupItem` (Radix) fornisce la
 * semantica `role="radiogroup"` + `role="radio"` + navigazione con le frecce e
 * `aria-checked` gratuitamente. Ogni opzione è avvolta in un `Card` con una
 * descrizione a una riga. La `Card` è cliccabile tramite `<label>` associato
 * all'item (click sull'intera card = selezione dell'item), mantenendo l'unico
 * controllo focusabile/tabbabile nel `RadioGroupItem` reale.
 *
 * Pattern seguiti (plan IMPL-P3-001):
 *  - Stepper.tsx (convenzioni ARIA/tastiera del wizard) → qui coperte da Radix.
 *  - shadcn RadioGroup/RadioGroupItem.
 *
 * L'enum dei valori arriva da `@app/shared` (`AdempimentoType`) — nessuna
 * duplicazione locale (Key Decision).
 */
import type { AdempimentoType } from '@app/shared';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface AdempimentoTypeSelectorProps {
  value: AdempimentoType | undefined;
  onChange: (v: AdempimentoType) => void;
  /** id opzionale del `<Label>`/heading esterno che descrive il gruppo. */
  'aria-labelledby'?: string;
}

interface AdempimentoOption {
  value: AdempimentoType;
  label: string;
  description: string;
}

const OPTIONS: ReadonlyArray<AdempimentoOption> = [
  {
    value: 'PROROGA',
    label: 'Proroga',
    description: 'Estendi la durata del contratto',
  },
  {
    value: 'RISOLUZIONE',
    label: 'Risoluzione',
    description: 'Chiudi il contratto anticipatamente o alla scadenza',
  },
  {
    value: 'CESSIONE',
    label: 'Cessione',
    description: 'Trasferisci il contratto a un nuovo inquilino',
  },
];

export function AdempimentoTypeSelector({
  value,
  onChange,
  'aria-labelledby': ariaLabelledBy,
}: AdempimentoTypeSelectorProps) {
  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={(v) => onChange(v as AdempimentoType)}
      aria-label={ariaLabelledBy ? undefined : 'Tipo di adempimento'}
      aria-labelledby={ariaLabelledBy}
      className="grid gap-3 sm:grid-cols-3"
    >
      {OPTIONS.map((opt) => {
        const itemId = `adempimento-type-${opt.value.toLowerCase()}`;
        const descId = `${itemId}-desc`;
        const isSelected = value === opt.value;
        return (
          <Card
            key={opt.value}
            className={cn(
              'relative cursor-pointer p-4 transition-colors hover:border-primary/60',
              isSelected && 'border-primary ring-1 ring-primary',
            )}
          >
            <div className="flex items-start gap-3">
              <RadioGroupItem
                value={opt.value}
                id={itemId}
                aria-describedby={descId}
                className="mt-1"
              />
              <div className="space-y-1">
                <Label htmlFor={itemId} className="cursor-pointer text-base font-medium">
                  {opt.label}
                </Label>
                <p id={descId} className="text-sm text-muted-foreground">
                  {opt.description}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
    </RadioGroup>
  );
}
